import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import EsimPlans from "./EsimPlans/EsimPlans";
import "./esim.scss";
import SearchTabs from "../Home/SearchTabs/SearchTabs";
import { get } from "../../utils/request";
import { getContext, getTabId, getTimezone } from "../../utils/context";
import EsimLocationDialog from "../../components/ui/commons/EsimLocationDialog";
import ErrorModal from "../ui/commons/ErrorModal";
import { AppError } from "../../utils/customError";
import SEO, { SITE_URL } from "../SEO/SEO";

const QUICK_ESIM_DESTINATIONS = [
  { label: "Singapore", query: "locationType=local&location=SG" },
  { label: "Malaysia", query: "locationType=local&location=MY" },
  { label: "Japan", query: "locationType=local&location=JP" },
  { label: "Thailand", query: "locationType=local&location=TH" },
  { label: "Global", query: "locationType=global" },
];

const Esim = () => {
  const [searchParams] = useSearchParams();
   
  const [locationType, setLocationType] = useState(null);
  const [location, setLocation] = useState(null);

  const [plans, setPlans] = useState([]);
  const [loader, setLoader] = useState(true);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [selected, setSelected] = useState(-1);

  const [showEsimLocationDialog, setShowEsimLocationDialog] = useState(false);
  const [clickedCoveragePlan, setClickedCoveragePlan] = useState(null);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchEsimPackages = async () => {
    try {
      if (!locationType || (locationType !== 'global' && !location)) {
        return;
      }

      setPackagesLoading(true);
      const tabId = getTabId();
      const context = getContext();
      const isLocalDev =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      let packages;

      if (isLocalDev) {
        const params = new URLSearchParams({
          locationType,
          location: location || "",
          t: `${Date.now()}`,
        });
        const response = await fetch(`/api/esim/search?${params.toString()}`, {
          headers: {
            "X-Ctx-Client-Id": context.clientId,
            "X-Ctx-Client-Type": context.clientType,
            "X-Ctx-Currency": context.currency,
            "X-Ctx-Timezone": getTimezone(),
            "X-Ctx-Tab-Id": tabId,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to load eSIM plans.");
        }

        const result = await response.json();
        if (result?.status === false) {
          throw new AppError(result.message, result.errorCode);
        }
        packages = result?.data || [];
      } else {
        packages = await get(
          "/esim/search",
          { locationType, location },
          { "X-Ctx-Tab-Id": tabId }
        );
      }

      setPlans(packages);
      setPackagesLoading(false);
    } catch (error) {
      console.log(error);
      setPlans([]);
      setPackagesLoading(false);
    }
  };

  useEffect(() => {
    let locationTypeValue = searchParams.get("locationType");
    let locationValue = searchParams.get("location");

    if (!locationTypeValue || (locationTypeValue !== 'global' && !locationValue)) {
      setLocationType(null);
      setLocation(null);
      setPlans([]);
      setLoader(false);
      setPackagesLoading(false);
      return;
    }

    setLocationType(locationTypeValue);
    setLocation(locationValue);
   
    setLoader(false);
  }, [searchParams]);

  useEffect(() => {
    fetchEsimPackages();
    // Keep the original fetch timing tied only to parsed URL search state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationType, location]);

  function onClickCoverage(plan) {
    setClickedCoveragePlan(plan);
    setShowEsimLocationDialog(true);
  }

  function showErrorInfo(errorMessage) {
    setErrorMessage(errorMessage);
    setShowError(true);
  }

  function onErrorConfirmed() {
    fetchEsimPackages();
    setSelected(-1);
    setShowError(false);
  }

  return (
    <>
      <SEO
        title="Compare Travel eSIM Plans | Local, Regional & Global Data | Teleport Now"
        description="Search local, regional, and global travel eSIM plans with Teleport Now. Compare coverage, data size, validity, and price before you fly."
        canonicalPath="/esim"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${SITE_URL}/esim#webpage`,
              name: "Compare Travel eSIM Plans",
              description:
                "Search local, regional, and global travel eSIM plans by destination, coverage, data size, validity, and price.",
              url: `${SITE_URL}/esim`,
              isPartOf: {
                "@type": "WebSite",
                "@id": `${SITE_URL}/#website`,
                name: "Teleport Now",
                url: SITE_URL,
              },
              about: {
                "@type": "Service",
                name: "Travel eSIM comparison",
                provider: {
                  "@type": "Organization",
                  "@id": `${SITE_URL}/#organization`,
                  name: "Teleport Now",
                },
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: SITE_URL,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "eSIM",
                  item: `${SITE_URL}/esim`,
                },
              ],
            },
          ],
        }}
      />
      <Navbar />
      <SearchTabs />

      {loader ? (
        <div className="esim-loader"></div>
      ) : !locationType || (locationType !== "global" && !location) ? (
        <section className="esim-empty-state">
          <div className="esim-empty-state__copy">
            <p className="esim-empty-state__eyebrow">Travel eSIMs</p>
            <h1>Choose where you are going and compare data plans.</h1>
            <p>
              Start with the eSIM search above, or jump into a popular
              destination to see available coverage in the Teleport Now app flow.
            </p>
            <div className="esim-empty-state__quick-links">
              {QUICK_ESIM_DESTINATIONS.map((destination) => (
                <a
                  key={destination.label}
                  href={`/esim?${destination.query}`}
                  onClick={() => {
                    if (typeof window !== "undefined" && typeof window.gtag === "function") {
                      window.gtag("event", "esim_quick_destination_click", {
                        destination: destination.label,
                      });
                    }
                  }}
                >
                  {destination.label}
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <>
          <div className="esim-layout">
            {/* Left side - Image */}
            {/* <div className="esim-layout__image">
              <img
                src="/images/argentina.png"
                alt={`eSIM for ${locationType}`}
                className="image"
              />
            </div> */}

            {/* Right side - Content */}
            <div className="esim-layout__content">
              {packagesLoading ? (
                <div className="esim-loader"></div>
              ) : (
                <EsimPlans
                  plans={plans}
                  selected={selected}
                  setSelected={setSelected}
                  onClickCoverage={onClickCoverage}
                  showErrorInfo={showErrorInfo}
                  locationType={locationType}
                  location={location}
                />
              )}
            </div>
          </div>
        </>
      )}

      {!!clickedCoveragePlan && (
        <EsimLocationDialog
          open={showEsimLocationDialog}
          onClose={() => setShowEsimLocationDialog(false)}
          countries={clickedCoveragePlan.locationNetworkList}
        />
      )}

      <ErrorModal
        open={showError}
        message={errorMessage}
        buttonText="OK"
        onConfirmed={onErrorConfirmed}
      />
      <Footer />
    </>
  );
};

export default Esim;
